import React, { useEffect, useRef, useState } from 'react';

import { fleetAPI } from '../../lib/api';

/**
 * Shared bits of the Fleet section. Colours come from the theme tokens in
 * index.css (--green, --next, --live, --blue, --faint) so every combination of
 * light/dark and standard/modern renders correctly; status is shown as a calm
 * tint, never a solid fill (Modern UI spec, §3b).
 */

export const POLL_MS = 2000;

const TONE_VAR = { ok: '--green', warn: '--next', bad: '--live', info: '--blue', off: '--faint' };

export function toneStyle(tone, strength = 14) {
  const v = TONE_VAR[tone] || TONE_VAR.off;
  return { color: `var(${v})`, background: `color-mix(in srgb, var(${v}) ${strength}%, transparent)` };
}

export function Chip({ tone = 'off', children, title }) {
  return (
    <span title={title} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap" style={toneStyle(tone)}>
      {children}
    </span>
  );
}

export function videoTone(state) {
  if (state === 'LIVE') return 'ok';
  if (state === 'NO_SIGNAL' || state === 'DEGRADED') return 'warn';
  if (state === 'NO_DEVICE' || state === 'DEVICE_LOST') return 'bad';
  if (state === 'STARTING' || state === 'FORMAT_CHANGE') return 'info';
  return 'off';
}

export function commsTone(state) {
  if (state === 'READY') return 'ok';
  if (state === 'TALKING') return 'info';
  if (state === 'CONNECTING' || state === 'RECONNECTING') return 'warn';
  if (state === 'OFFLINE' || state === 'AUTH_FAILED') return 'bad';
  return 'off';
}

/** Headset and programme are free-form strings from the box; read them loosely. */
export function presenceTone(value) {
  if (!value) return 'off';
  if (/missing|none|absent|lost|disconnected|no_/i.test(value)) return 'warn';
  if (/disabled|off|unknown/i.test(value)) return 'off';
  return 'ok';
}

export function tempTone(c) {
  if (c == null) return 'off';
  if (c >= 90) return 'bad';
  if (c >= 80) return 'warn';
  return 'ok';
}

export const SEVERITY_TONE = { critical: 'bad', warning: 'warn', info: 'info' };

/** Tally as a calm frame: red on program, green on preview, nothing otherwise. */
export function tallyFrame(tally, online) {
  if (!online) return {};
  if (tally === 'program') return { borderColor: 'var(--live)', boxShadow: 'inset 0 0 0 1px var(--live), 0 0 0 1px var(--live)' };
  if (tally === 'preview') return { borderColor: 'var(--green)', boxShadow: 'inset 0 0 0 1px var(--green)' };
  return {};
}

export const TALLY_LABEL = { program: 'PGM', preview: 'PVW', off: 'Off', unknown: 'Unknown' };
export const TALLY_TONE = { program: 'bad', preview: 'ok', off: 'off', unknown: 'off' };

export function ago(iso, now = Date.now()) {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}

export function apiError(err, fallback = 'That did not work') {
  return err?.response?.data?.error || err?.message || fallback;
}

/**
 * The box's latest thumbnail as an object URL. Fetched with the JWT as a blob
 * (an <img src> cannot carry it), refetched only when thumb_seq moves, and the
 * previous URL is revoked so a grid left open all day does not leak memory.
 */
export function useThumbnail(boxId, seq) {
  const [url, setUrl] = useState(null);
  const current = useRef(null);

  useEffect(() => {
    if (!boxId || !seq) return undefined;
    let cancelled = false;
    fleetAPI.thumbnail(boxId)
      .then((res) => {
        if (cancelled) return;
        const next = URL.createObjectURL(res.data);
        if (current.current) URL.revokeObjectURL(current.current);
        current.current = next;
        setUrl(next);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [boxId, seq]);

  useEffect(() => () => {
    if (current.current) URL.revokeObjectURL(current.current);
    current.current = null;
  }, []);

  return url;
}

/** The repo's modal pattern, on theme tokens. For anything that takes a camera off air. */
export function ConfirmDialog({ title, children, confirmLabel = 'Confirm', danger = false, busy = false, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={onCancel}>
      <div role="dialog" aria-modal="true" className="bg-app-panel border border-app-line-chrome rounded-lg w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-app-line-chrome">
          <h2 className="font-semibold text-app-fg">{title}</h2>
        </div>
        <div className="px-5 py-4 text-sm text-gray-300 space-y-2">{children}</div>
        <div className="px-5 py-3 border-t border-app-line-chrome flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded text-gray-400 hover:text-app-fg hover:bg-app-row-hover">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-4 py-2 text-sm rounded font-medium disabled:opacity-40"
            style={danger ? { background: 'var(--live)', color: '#fff' } : { background: 'var(--blue)', color: '#fff' }}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
